import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "../../../api/axios";
import HeaderWithBack from "../../../components/HeaderWithBack";
import Loading from "../../../components/Loading";
import { useAuthStore } from "../../../stores/authStores";
import { useCheckoutStore } from "../../../stores/checkoutStore";
import { formatRupiah } from "../../../utils/formatPrice";

interface VoucherData {
    id: number;
    code: string;
    discount_value: number;
    expired_at: string;
}

const VoucherPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const token = useAuthStore((state) => state.token);
    const { practitionerRoleId } = useParams<{ practitionerRoleId: string }>();
    const teleconsultationId = location.state?.teleconsultationId;
    const [vouchers, setVouchers] = useState<VoucherData[]>([]);
    const [manualCode, setManualCode] = useState("");
    const [loading,setLoading] = useState(true);
    const { selectedPayment, selectedVoucher: prevVoucher } = location.state || {};
    const pertanyaan = location.state?.pertanyaan || null;
    const selectedVoucher = useCheckoutStore((s) => s.selectedVoucher);
    const setVoucher = useCheckoutStore((s) => s.setVoucher);

    useEffect(() => {
        if (!token) return;

        axios
        .get("/voucher", {
            params: {
                teleconsult_id: teleconsultationId,
            },
                headers: {
                Authorization: `Bearer ${token}`,
            },
        })
        .then((res) => {
            setVouchers(res.data.data || []);
        })
        .catch((err) => {
            console.log("Voucher API Error:", err);
        })
        .finally(() => {
            setLoading(false)
        })
    }, [token, teleconsultationId]);

    const handleApplyVoucher = () => {
        navigate(`/checkout-teleconsultation/${practitionerRoleId}`, {
            state: {
                    selectedVoucher,
                    selectedPayment,
                    pertanyaan,
                },
        });
    };
  
    useEffect(() => {
        if (prevVoucher) {
            setVoucher(prevVoucher);
        }
    }, [prevVoucher]);

    const handleManualVoucher = () => {
        if (!manualCode) return;

        axios
        .post(
            "/voucher/apply",
            {
                teleconsult_id: teleconsultationId,
                voucher_code: manualCode,
            },
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }
        )
        .then((res) => {
            handleApplyVoucher();
        })
        .catch((err) => {
            alert(err.response?.data?.message || "Voucher tidak valid");
        });
    };

    return (
        <div className="min-h-screen bg-gray-100">
        <HeaderWithBack title="Voucher" />

        <div className="px-4 mt-4 flex gap-2">
            <input
                type="text"
                value={manualCode}
                onChange={(e) => setManualCode(e.target.value)}
                placeholder="Masukkan Kode Voucher"
                className="flex-1 border rounded-lg px-3 py-2"
            />
            <button
                onClick={handleManualVoucher}
                className="bg-teal-500 text-white px-4 rounded-lg font-semibold"
            >
            Pakai
            </button>
        </div>

        <div className="px-4 mt-4 space-y-3 pb-24">
            {loading ? (
                <Loading />
            ) : vouchers.length === 0 ? (
            <p className="text-sm text-gray-500 text-center mt-6">
                Tidak ada voucher tersedia
            </p>
            ) : (
            vouchers.map((v) => (
                <div
                    key={v.id}
                    onClick={() => {
                        if (selectedVoucher?.id === v.id) {
                            setVoucher(null);
                        } else {
                            setVoucher(v);
                        }
                    }}
                    className={`bg-white rounded-xl p-4 shadow flex justify-between cursor-pointer
                        ${selectedVoucher?.id === v.id ? "border-2 border-teal-500" : ""}
                    `}
                >
                <div>
                    <p className="font-semibold text-teal-600 text-sm mb-1">
                        {v.code}
                    </p>

                    <p className="text-xs text-gray-500">
                        Diskon {formatRupiah(v.discount_value)}
                    </p>

                    <p className="text-xs text-gray-400 mt-1">
                        Berlaku sampai{" "}
                        {new Date(v.expired_at).toLocaleDateString("id-ID")}
                    </p>
                </div>

                <input
                    type="radio"
                    checked={selectedVoucher?.id === v.id}
                    readOnly
                    className="mt-1 accent-teal-500"
                />
                </div>
            ))
            )}
        </div>

        <div className="fixed bottom-0 left-0 right-0 bg-white border-t px-4 py-3 flex justify-between items-center">
            <div>
            <p className="text-xs text-gray-500">Voucher dipilih</p>
            <p className="text-teal-600 font-bold">
                {selectedVoucher ? selectedVoucher.code : "Belum ada"}
            </p>
            </div>

            <button
                onClick={() => handleApplyVoucher()}
                className="px-6 py-2 rounded-lg font-semibold text-white bg-teal-500"
            >
            {selectedVoucher ? "Pakai Voucher" : "Lanjut Tanpa Voucher"}
            </button>
        </div>
    </div>
  );
};

export default VoucherPage;
